import { READ_TIMEOUT_MS, runInEditQueue } from "./editQueue";
import { paragraphOutline } from "./sectionHeading";
import { changeHash, textSplices, wordHash, wordQuery } from "./wordShell";
const NO_PARAGRAPH = -1;
const supports = (version)=>Office.context.requirements.isSetSupported("WordApi", version);
const isoDate = (date)=>{
    const parsed = date ? new Date(date) : null;
    return parsed && !Number.isNaN(parsed.getTime()) ? parsed.toISOString() : "";
};
const changeRow = (change)=>({
        type: String(change.type),
        author: change.author,
        date: isoDate(change.date),
        text: change.text
    });
async function loadOutlineLevels(context) {
    try {
        // A collection of its own: reloading the snapshot's collection replaces its items, dropping what they loaded.
        const paragraphs = context.document.body.getRange("Whole").paragraphs.load("items/outlineLevel");
        await context.sync();
        return paragraphs.items.map((paragraph)=>paragraph.outlineLevel);
    } catch  {
        return null;
    }
}
export async function readSnapshot() {
    return Word.run(async (context)=>{
        const { document } = context;
        const paragraphs = document.body.paragraphs.load("items/text,items/uniqueLocalId,items/style,items/styleBuiltIn,items/tableNestingLevel,items/isListItem");
        const selection = document.getSelection().load("text");
        const selectedFirst = selection.paragraphs.getFirstOrNullObject().load("uniqueLocalId");
        const selectedLast = selection.paragraphs.getLastOrNullObject().load("uniqueLocalId");
        const changes = supports("1.6") ? document.body.getTrackedChanges().load("items/author,items/date,items/type,items/text") : null;
        const comments = supports("1.4") ? document.body.getComments().load("items/id,items/authorName,items/resolved,items/content") : null;
        await context.sync();
        const outlineLevels = await loadOutlineLevels(context);
        const lists = paragraphs.items.map((paragraph)=>paragraph.isListItem ? paragraph.listItemOrNullObject.load("level,listString") : null);
        const cells = paragraphs.items.map((paragraph)=>paragraph.tableNestingLevel > 0 ? paragraph.parentTableCellOrNullObject.load("rowIndex,cellIndex") : null);
        const changeStarts = (changes?.items ?? []).map((change)=>change.getRange().paragraphs.getFirstOrNullObject().load("uniqueLocalId"));
        const commentRanges = (comments?.items ?? []).map((comment)=>comment.getRange().load("text"));
        const commentStarts = commentRanges.map((range)=>range.paragraphs.getFirstOrNullObject().load("uniqueLocalId"));
        await context.sync();
        const indexOf = new Map(paragraphs.items.map((paragraph, index)=>[
                paragraph.uniqueLocalId,
                index
            ]));
        const pgOf = (paragraph)=>paragraph.isNullObject ? NO_PARAGRAPH : indexOf.get(paragraph.uniqueLocalId) ?? NO_PARAGRAPH;
        const from = pgOf(selectedFirst);
        return {
            paragraphs: paragraphs.items.map((paragraph, i)=>{
                const list = lists[i];
                const cell = cells[i];
                return {
                    i,
                    uid: paragraph.uniqueLocalId,
                    hash: wordHash(paragraph.text),
                    style: paragraph.style,
                    outline: paragraphOutline({
                        text: paragraph.text,
                        styleBuiltIn: paragraph.styleBuiltIn,
                        tableNestingLevel: paragraph.tableNestingLevel,
                        outlineLevel: outlineLevels?.[i]
                    }),
                    list: list && !list.isNullObject ? {
                        level: list.level,
                        label: list.listString
                    } : null,
                    table: cell && !cell.isNullObject ? {
                        depth: paragraph.tableNestingLevel,
                        row: cell.rowIndex,
                        cell: cell.cellIndex
                    } : null,
                    text: paragraph.text
                };
            }),
            selection: from === NO_PARAGRAPH ? null : {
                from,
                to: Math.max(from, pgOf(selectedLast)),
                text: selection.text
            },
            changes: (changes?.items ?? []).map((change, i)=>{
                const row = changeRow(change);
                return {
                    i,
                    pg: pgOf(changeStarts[i]),
                    hash: changeHash(row),
                    ...row
                };
            }),
            comments: (comments?.items ?? []).map((comment, i)=>({
                    i,
                    pg: pgOf(commentStarts[i]),
                    id: comment.id,
                    author: comment.authorName,
                    resolved: comment.resolved,
                    anchor: commentRanges[i].text,
                    text: comment.content
                }))
        };
    });
}
function searchTokens(paragraph, splices) {
    const finds = new Set(splices.flatMap(({ from, to })=>[
            from.find,
            to.find
        ]));
    return new Map([
        ...finds
    ].map((find)=>[
            find,
            paragraph.search(wordQuery(find), {
                matchCase: true
            }).load("items")
        ]));
}
function tokensOf(splices, searches) {
    const tokenOf = ({ find, nth, count })=>{
        const items = searches.get(find)?.items;
        return items?.length === count ? items[nth] : null;
    };
    const tokens = splices.map(({ from, to })=>({
            from: tokenOf(from),
            to: tokenOf(to)
        }));
    return tokens.every((token)=>!!token.from && !!token.to) ? tokens : null;
}
const pointOf = (token, { at })=>token.getRange(at === "start" ? Word.RangeLocation.start : Word.RangeLocation.end);
function spliceText(paragraph, before, after) {
    const splices = textSplices(before, after);
    const searches = splices && searchTokens(paragraph, splices);
    return ()=>{
        const tokens = splices && searches && tokensOf(splices, searches);
        if (!splices || !tokens) {
            paragraph.getRange(Word.RangeLocation.content).insertText(after, Word.InsertLocation.replace);
            return;
        }
        const regions = splices.map(({ from, to, remove }, index)=>remove ? pointOf(tokens[index].from, from).expandTo(pointOf(tokens[index].to, to)) : null);
        splices.forEach(({ to, insert }, index)=>{
            const region = regions[index];
            if (region && insert) region.insertText(insert, Word.InsertLocation.replace);
            else if (region) region.delete();
            else tokens[index].to.insertText(insert, to.at === "end" ? Word.InsertLocation.after : Word.InsertLocation.before);
        });
    };
}
export async function redlineParagraph(context, paragraph, text) {
    paragraph.load("text");
    await context.sync();
    const apply = spliceText(paragraph, paragraph.text, text);
    await context.sync();
    apply();
    await context.sync();
}
export function watchDocument(onChange) {
    if (!supports("1.6")) return ()=>undefined;
    const handler = ()=>onChange();
    const onParagraph = async ()=>onChange();
    let isStopped = false;
    const office = Office.context.document;
    office.addHandlerAsync(Office.EventType.DocumentSelectionChanged, handler);
    const paragraphEvents = runInEditQueue(()=>Word.run(async (context)=>{
            const { document } = context;
            const results = [
                document.onParagraphAdded.add(onParagraph),
                document.onParagraphChanged.add(onParagraph),
                document.onParagraphDeleted.add(onParagraph)
            ];
            await context.sync();
            return {
                document,
                results
            };
        }), READ_TIMEOUT_MS).catch((error)=>{
        console.warn("[word] paragraph events unavailable:", error);
        return null;
    });
    return ()=>{
        if (isStopped) return;
        isStopped = true;
        office.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, {
            handler
        });
        paragraphEvents.then((events)=>events ? runInEditQueue(()=>Word.run(events.document, async (context)=>{
                    events.results.forEach((result)=>result.remove());
                    await context.sync();
                }), READ_TIMEOUT_MS) : undefined).catch((error)=>console.warn("[word] could not remove paragraph events:", error));
    };
}
